import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import i18n from 'i18n';
import apiCubic from 'config/api-cubic';
import requester from 'utils/requester';
import { hideLoader, showLoader } from 'actions/loader';
import { showToast } from 'actions/toast';
import { hideModal, showModal } from 'actions/modal';
import { getProfileData } from 'reducers/profile/selectors';
import { INIT_UI_LANGUAGE } from 'constants/languages';
import { MODAL_TYPES } from 'constants/modal-types';
import Header from './common/Header';
import OTPForm from './common/OTPForm';
import ActionsSection from './ActionsSection';
import { StyledWhiteText } from './StyledComponents';
import useOnSuccessLogin from 'hooks/useOnSuccessLogin';
import useOnNavigationBack from 'hooks/useOnNavigationBack';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';

const i18nScope = 'authorizationPhoneActivationPage';
const SECTION_NAME = 'OTP_INPUT';
const RESEND_CODE_THRESHOLD_SECONDS = 59;
const MAX_OTP_ATTEMPS = 10;
const code = '+380';

const ERROR_TO_RESTART_TIMER = 'registration_otp_validation_error';

const getDescription = ({ code, phone }) => {
  return [
    i18n.t(`${i18nScope}.description`),
    <br key="br" />,
    <StyledWhiteText key="login-value">{code + phone}</StyledWhiteText>,
  ];
};

const PhoneActivationPage = () => {
  const history = useHistory();
  const isPhoneAddedRef = useRef(false);
  const phone = new URLSearchParams(history.location.search).get('phone') || '';
  const { contractNumber, customerRef } = useSelector(getProfileData);
  const dispatch = useDispatch();
  const [timeToNextResend, setTimeToNextResend] = useState(RESEND_CODE_THRESHOLD_SECONDS);
  const [attemps, setAttemps] = useState(0);

  const onSuccessLogin = useOnSuccessLogin();

  const updateTimeToNextResend = useCallback(
    () => setTimeToNextResend((value) => value && value - 1),
    [setTimeToNextResend],
  );

  const onSubmit = useCallback(
    async (otp) => {
      let currentAttemptNumber = attemps + 1;

      const authToken = apiCubic.getAuthToken();

      try {
        dispatch(showLoader());

        const data = await requester({
          method: 'post',
          url: `${apiCubic.host}${apiCubic.endpoints.phoneActivation}`,
          appendParams: false,
          extendHeadersWithSession: false,
          headers: { Authorization: `Basic ${authToken}` },
          data: {
            otp,
            contractCode: contractNumber,
            customer: customerRef,
            phone: `0${phone}`,
            language: INIT_UI_LANGUAGE,
          },
        });

        innerAnalyticsVolia.postEvent({
          eventGroup: 'alternative sign in screen',
          eventName: 'add phone to contract',
          eventValue: data.error ? data.error.code : 'success',
          addInfo: `0${phone}`,
        });

        if (data.error) {
          if (data.error.code === ERROR_TO_RESTART_TIMER) setTimeToNextResend(0);
          throw new Error(data.error.message);
        }

        isPhoneAddedRef.current = true;

        const toastText = i18n.t(`${i18nScope}.success`);

        dispatch(showToast({ options: { toastText } }));

        await onSuccessLogin({ contractNumber });
      } catch (error) {
        console.error(error);
        dispatch(
          showModal(MODAL_TYPES.INFO, {
            onModalBack() {
              dispatch(hideModal());
              SpatialNavigationWrapper.focus(`@${SECTION_NAME}`);
            },
            message: error.message,
          }),
        );
      } finally {
        dispatch(hideLoader());
      }

      if (currentAttemptNumber >= MAX_OTP_ATTEMPS) {
        setTimeToNextResend(0);
        currentAttemptNumber = 0;
      }
      setAttemps(currentAttemptNumber);
    },
    [attemps, dispatch, contractNumber, customerRef, phone, onSuccessLogin],
  );

  const onResend = useCallback(async () => {
    try {
      const authToken = apiCubic.getAuthToken();

      dispatch(showLoader());

      await requester({
        method: 'post',
        url: `${apiCubic.host}${apiCubic.endpoints.phoneActivationOTP}`,
        appendParams: false,
        extendHeadersWithSession: false,
        headers: { Authorization: `Basic ${authToken}` },
        data: { contractCode: contractNumber, phone: `0${phone}`, language: INIT_UI_LANGUAGE },
      });
    } finally {
      dispatch(hideLoader());
      setTimeToNextResend(RESEND_CODE_THRESHOLD_SECONDS);
    }
  }, [dispatch, phone, contractNumber]);

  useEffect(() => {
    const intervalId = setInterval(updateTimeToNextResend, 1000);
    return () => clearInterval(intervalId);
  });

  useOnNavigationBack(() => {
    // https://hootko.atlassian.net/browse/STVWL-573
    // Don't show modal if it's return from success auth
    if (isPhoneAddedRef.current) return;

    dispatch(
      showModal(MODAL_TYPES.PHONE, {
        contractNumber,
        onModalConfirm({ phone }) {
          dispatch(hideModal());
          const searchParams = new URLSearchParams(history.location.search);
          searchParams.append('phone', phone);
          history.push({ pathname: '/profile/authorization/phone-activation', search: searchParams.toString() });
        },
        onModalBack() {
          dispatch(hideModal());
          onSuccessLogin({ contractNumber });
        },
      }),
    );
  });

  return (
    <>
      <Header title={i18n.t(`${i18nScope}.title`)} description={getDescription({ code, phone })} isWideBlock />
      <OTPForm
        sectionName={SECTION_NAME}
        buttonLabel={i18n.t(`${i18nScope}.next`)}
        invalidMessage={i18n.t(`${i18nScope}.otpInvalid`)}
        onSubmit={onSubmit}
      >
        {({ resetInput }) => {
          return (
            <ActionsSection.Button
              label={i18n.t(`${i18nScope}.${timeToNextResend <= 0 ? 'resendCode' : 'resendCodeTimer'}`, {
                seconds: `0${timeToNextResend}`.slice(-2),
              })}
              onEnter={() => {
                onResend();
                resetInput();
              }}
              disabled={timeToNextResend > 0}
            />
          );
        }}
      </OTPForm>
    </>
  );
};

export default PhoneActivationPage;
